import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { listGameResults, getQuiz, deleteGameResult } from '../services/quizStorageService';

interface AggregateStats {
  total_players: number;
  total_questions: number;
  class_average_score: number;
  class_average_percent: number;
  median_score: number;
  std_deviation: number;
  score_distribution: { [key: string]: number };
}

interface TimingEvent {
  player_id: string;
  question_id: string;
  server_question_sent: string;
  client_question_received: string;
  client_answer_sent: string;
  server_answer_received: string;
  server_reveal_sent: string;
  network_lag_ms: number;
  effective_time_ms: number;
}

interface PhaseLagStats {
  average_lag_ms: number;
  median_lag_ms: number;
  max_lag_ms: number;
  min_lag_ms: number;
  sample_count: number;
}

interface ProblematicPlayer {
  player_id: string;
  average_lag_ms: number;
  issue_count: number;
  affected_questions: string[];
}

interface ProblematicQuestion {
  question_id: string;
  average_lag_ms: number;
  players_affected: number;
}

interface NetworkHealthSummary {
  average_network_lag_ms: number;
  median_lag_ms: number;
  std_dev_lag_ms: number;
  sample_count: number;
  problematic_players?: ProblematicPlayer[];
  problematic_questions?: ProblematicQuestion[];
  reading_phase_lag?: PhaseLagStats;
  question_phase_lag?: PhaseLagStats;
  reveal_phase_lag?: PhaseLagStats;
}

interface SavedGameResult {
  result_id: string;
  label: string;
  game_pin: string;
  played_at: string;
  duration_minutes: number;
  aggregate_stats: AggregateStats;
  question_stats: any[];
  player_results: any[];
  quiz_snapshot: any;
  timing_events?: TimingEvent[];
  network_health_summary?: NetworkHealthSummary;
}

const QuizResults: React.FC = () => {
  const { quizId } = useParams<{ quizId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<SavedGameResult[]>([]);
  const [quizTitle, setQuizTitle] = useState<string>('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  useEffect(() => {
    const fetchResults = async () => {
      if (!quizId) {
        setError('No quiz ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch quiz details for the title
        const quiz = await getQuiz(quizId);
        setQuizTitle(quiz.title);

        // Fetch saved game results
        const response = await listGameResults(quizId);
        setResults(response.results || []);
      } catch (err) {
        console.error('Failed to fetch results:', err);
        setError(err instanceof Error ? err.message : 'Failed to load results');
      } finally {
        setLoading(false);
      }
    };

    fetchResults();
  }, [quizId]);

  const handleViewResult = (result: SavedGameResult) => {
    // Navigate to quiz statistics page with the saved statistics
    navigate('/quiz-statistics', {
      state: { statistics: result }
    });
  };

  const handleDeleteClick = (resultId: string) => {
    setDeleteConfirm(resultId);
  };

  const handleDeleteConfirm = async (resultId: string) => {
    try {
      setDeleting(resultId);
      await deleteGameResult(resultId);

      // Remove from local state
      setResults(prevResults => prevResults.filter(r => r.result_id !== resultId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Failed to delete result:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete result');
    } finally {
      setDeleting(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-6 py-8">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
            <p className="mt-4 text-gray-600">Loading results...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="container mx-auto px-6 py-8">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-red-600">Error Loading Results</h1>
            <p className="mt-2 text-gray-600">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="mt-4 px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="container mx-auto px-6 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Game Results</h1>
                <p className="text-gray-600">{quizTitle}</p>
              </div>
              <button
                onClick={() => navigate('/')}
                className="px-6 py-2 bg-gray-500 text-white font-semibold rounded-lg hover:bg-gray-600 transition-colors"
              >
                Back to Home
              </button>
            </div>
          </div>

          {/* Results List */}
          {results.length === 0 ? (
            <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-8 text-center">
              <div className="text-gray-400 mb-4">
                <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">No Saved Results</h2>
              <p className="text-gray-600">
                Play this quiz and save the results to see them here.
              </p>
              <button
                onClick={() => navigate(`/host-quiz/${quizId}`)}
                className="mt-6 px-6 py-2 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors"
              >
                Host Quiz
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {results.map((result) => (
                <div
                  key={result.result_id}
                  className="bg-white rounded-lg shadow-lg border border-gray-200 p-6 hover:border-orange-300 transition-colors"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {result.label}
                      </h3>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          {new Date(result.played_at).toLocaleString()}
                        </div>
                        {result.aggregate_stats && (
                          <>
                            <div className="flex items-center">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                              {result.aggregate_stats.total_players} players
                            </div>
                            <div className="flex items-center">
                              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                              </svg>
                              {result.aggregate_stats.class_average_percent.toFixed(1)}% avg
                            </div>
                          </>
                        )}
                        {result.game_pin && (
                          <div className="flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                            </svg>
                            PIN: {result.game_pin}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="ml-4 flex gap-2">
                      <button
                        onClick={() => handleViewResult(result)}
                        className="px-6 py-2 bg-orange-500 text-white font-semibold rounded-lg hover:bg-orange-600 transition-colors"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => handleDeleteClick(result.result_id)}
                        disabled={deleting === result.result_id}
                        className="px-4 py-2 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Delete result"
                      >
                        {deleting === result.result_id ? (
                          <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="bg-red-100 rounded-full p-3 mr-4">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900">Delete Game Result</h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this game result? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={handleDeleteCancel}
                disabled={deleting === deleteConfirm}
                className="px-6 py-2 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteConfirm(deleteConfirm)}
                disabled={deleting === deleteConfirm}
                className="px-6 py-2 bg-red-500 text-white font-semibold rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting === deleteConfirm ? (
                  <span className="flex items-center">
                    <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Deleting...
                  </span>
                ) : (
                  'Delete'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizResults;
